import { readStoredPreviewGrant } from "./previewGrant";
import { readStoredAnonSessionCarry } from "./anonSession";

/**
 * Thin fetch wrapper used by every SPA → server call.
 *
 *   - `credentials: 'include'` so the anon cookie is sent on cross-origin requests
 *     during Vite dev (the prod build is same-origin and would carry the cookie
 *     anyway, but `include` is harmless there).
 *   - Mutations carry the CSRF token via the `X-CSRF-Token` header; the AuthProvider
 *     refreshes the token on a 403 and retries the call exactly once.
 *   - Every request carries `X-Client-Timezone` (IANA, from the browser's
 *     Intl). Server middleware on date-boundary routes validates the header
 *     strictly and returns 400 when absent — see `src/lib/requestTimezone.ts`.
 *   - Errors are normalised to an `ApiError` so screens can show the engine's
 *     `userSafeMessage` (when present) without branching on shape.
 *
 * Keep this module dependency-free so it can be imported from anywhere in the
 * SPA — including the error boundary, which uses it to report itself.
 */

/** Browser-detected IANA timezone, evaluated once per page load. */
function clientTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export type ApiCallOptions = {
  method?: HttpMethod;
  /** JSON body — serialised + content-type set automatically. */
  body?: unknown;
  /** Query params appended to the URL. */
  query?: Record<string, string | number | undefined>;
  /** When set, sent as the `X-CSRF-Token` header. */
  csrfToken?: string;
  /** AbortSignal for cancellation. */
  signal?: AbortSignal;
  /**
   * Pass `fetch`'s keepalive flag — lets a beacon-style request (impression
   * events, spec §2.5) survive navigation/unload while still carrying the
   * principal + CSRF headers `navigator.sendBeacon` can't.
   */
  keepalive?: boolean;
};

/** True when `fetch` (or our wrappers) rejected because the request was cancelled. */
export function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === "AbortError") return true;
  if (err instanceof Error && err.name === "AbortError") return true;
  if (err instanceof Error && /aborted/i.test(err.message)) return true;
  return false;
}

/**
 * Friendly message for an unknown thrown value. Preference order:
 *   1. `ApiError.userSafeMessage` — curated copy from the server.
 *   2. A status-code friendly fallback (network/auth/server) — covers the
 *      `apiCall` `Request failed (502)` path where no message field was sent.
 *   3. Caller's `fallback` string.
 *
 * Errors whose `.message` is the technical `apiCall` fallback (`Request failed
 * (NNN)`) are treated as "no useful detail" and replaced with the status map.
 * Aborts return the fallback — they should not be shown.
 */
export function userFacingError(err: unknown, fallback: string): string {
  if (isAbortError(err)) return fallback;
  if (err instanceof ApiError) {
    if (err.userSafeMessage && err.userSafeMessage.trim().length > 0) {
      return err.userSafeMessage;
    }
    // A failed CSRF check is a stale-token problem, not a permissions one — the
    // generic 403 copy ("You don't have permission…") sent debugging down the
    // wrong path. Surface the actionable fix instead. (Checked before the status
    // map because `csrf_invalid` is a 403.)
    if (err.code === "csrf_invalid") {
      return "Your session needs a refresh. Reload the page and try again.";
    }
    const mapped = friendlyForStatus(err.status);
    if (mapped) return mapped;
    if (err.message && !isTechnicalFallback(err.message)) return err.message;
    return fallback;
  }
  if (err instanceof Error) {
    if (err.message && !isTechnicalFallback(err.message)) return err.message;
    return fallback;
  }
  return fallback;
}

function isTechnicalFallback(message: string): boolean {
  return /^Request failed \(\d+\)$/.test(message.trim());
}

function friendlyForStatus(status: number): string | null {
  if (status === 0) return "We can't reach the server right now. Check your connection and try again.";
  if (status === 401 || status === 419) return "Your session expired. Refresh the page to sign back in.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "We couldn't find what you were looking for.";
  if (status === 410) return "This link has expired. Ask whoever sent it for a fresh one.";
  if (status === 408 || status === 504) return "That took too long. Try again in a moment.";
  if (status === 409) return "That conflicts with something already saved. Refresh and try again.";
  if (status === 429) return "Too many requests — give it a moment, then try again.";
  // Deliberately NO 400/422 case: callers pass a specific, user-friendly `fallback`
  // for validation failures (e.g. "Couldn't remove Berlin Marathon — try again")
  // and a generic 400 map entry would shadow every one of them (F-X1-4 — the real
  // fix is per-route userSafeMessage server-side, not a generic client string).
  if (status >= 500 && status < 600) return "We're having trouble reaching the server. Try again in a moment.";
  return null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly userSafeMessage?: string;
  readonly body: unknown;
  constructor(status: number, code: string, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
    if (body && typeof body === "object" && "userSafeMessage" in body) {
      this.userSafeMessage = String((body as { userSafeMessage?: unknown }).userSafeMessage ?? "");
    }
  }
}

let cachedApiBase: string | null = null;

/**
 * App-proxy base the SPA shell was served under (e.g. `/apps/fuel-plans`), read
 * once from the mount element's `data-app-proxy-base`. Root-relative `/api/...`
 * calls are prefixed with it so storefront-origin fetches reach the app through
 * the Shopify app proxy. Empty during Vite dev (same-origin) — unchanged there.
 */
function apiBase(): string {
  if (cachedApiBase === null) {
    const el =
      typeof document !== "undefined"
        ? document.getElementById("customer-app-root")
        : null;
    cachedApiBase = (el?.getAttribute("data-app-proxy-base") ?? "").replace(/\/+$/, "");
  }
  return cachedApiBase;
}

/**
 * App-proxy base path the SPA was served under (e.g. `/apps/fuel-plans`), or
 * `""` in Vite dev. Use it to build absolute in-app URLs (share links etc.).
 */
export function appProxyBase(): string {
  return apiBase();
}

/** Mirrors the server's `CustomerAccountModel` (src/modules/shopify/storefrontAuthUrl.ts). */
export type CustomerAccountModel = "classic" | "new_customer_accounts";

/**
 * Storefront account-login URL with a return path back to where the visitor is
 * now, so after signing in the SPA reboots as a customer (and any anon session
 * merges). Mirrors the server's gate in `src/modules/shopify/middleware.ts` and
 * the shared `buildStorefrontAuthUrl` helper.
 *
 * PURE — the caller supplies `accountModel` (from the auth-context `shop`) and an
 * optional `returnPath`. When `returnPath` is omitted we derive `pathname +
 * search` from `window` (the planner reads deep-link params `?import=1`,
 * `?week=…`, so preserving the search returns the guest to the same state; the
 * hash is intentionally dropped). Either way the path is sanitized to a
 * single-leading-slash, same-origin relative path — protocol-relative (`//host`)
 * or non-slash values fall back to the planner, so no caller can smuggle in a
 * cross-origin redirect.
 *
 * Two account models, matching the server:
 *   - "classic"               → `/account/login?return_url=<encoded>`
 *   - "new_customer_accounts" → `/customer_authentication/login?return_to=<encoded relative>`
 */
export function accountLoginUrl(
  accountModel: CustomerAccountModel = "classic",
  returnPath?: string
): string {
  let returnTo = `${apiBase()}/my-training`;
  const candidate =
    returnPath ??
    (typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : undefined);
  // Must be a single-leading-slash, same-origin path. Reject protocol-relative
  // (`//host`) and anything that doesn't start with `/`.
  if (candidate && candidate.startsWith("/") && !candidate.startsWith("//")) {
    returnTo = candidate;
  }
  const encoded = encodeURIComponent(returnTo);
  return accountModel === "new_customer_accounts"
    ? `/customer_authentication/login?return_to=${encoded}`
    : `/account/login?return_url=${encoded}`;
}

/**
 * Dev-only: the hot paths whose latency the dashboard audit (§2.4) cares about.
 * Matched by pathname prefix so query strings (`?week=…`) don't defeat the
 * check. Only referenced behind `import.meta.env.DEV`, so it's tree-shaken out
 * of the prod bundle along with the timing block.
 */
const TIMED_FETCH_PREFIXES = [
  "/api/dashboard",
  "/api/journeys",
  // Audit §2.4 named "/api/planner/workouts"; the actual workout-list route is
  // "/api/planned-workouts". Cover both so the real choke point is captured.
  "/api/planner/workouts",
  "/api/planned-workouts",
  "/api/events"
];
function shouldTimeFetch(path: string): boolean {
  const pathname = path.split("?")[0];
  return TIMED_FETCH_PREFIXES.some((p) => pathname.startsWith(p));
}

function buildUrl(path: string, query?: ApiCallOptions["query"]): string {
  const full = path.startsWith("/api/") ? `${apiBase()}${path}` : path;
  if (!query) return full;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  if (!s) return full;
  return full.includes("?") ? `${full}&${s}` : `${full}?${s}`;
}

export async function apiCall<T>(path: string, opts: ApiCallOptions = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Client-Timezone": clientTimezone()
  };
  const isMutation = method !== "GET";
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  if (isMutation && opts.csrfToken) {
    headers["X-CSRF-Token"] = opts.csrfToken;
  }
  const previewGrant = readStoredPreviewGrant();
  if (previewGrant) {
    headers["X-FG-Preview-Grant"] = previewGrant;
  }
  // Anon-session carry token — the cookie is stripped by the Shopify app proxy,
  // so this keeps the guest bound to ONE session across requests. This is the
  // signed cookie wire value, never the raw `anonSessionId` (do not "optimize"
  // by sending the id — it is published in /context and is not a credential).
  const anonCarry = readStoredAnonSessionCarry();
  if (anonCarry) {
    headers["X-FG-Anon-Session"] = anonCarry;
  }

  // Dev-only per-request timing for the dashboard "20s tail" audit (§2.4).
  // Zero-cost in prod: the whole block is behind `import.meta.env.DEV`, which
  // Vite statically replaces with `false` and dead-code-eliminates. Only the
  // handful of hot paths are logged so the console stays readable.
  const timedPath =
    import.meta.env.DEV && shouldTimeFetch(path) ? performance.now() : null;

  const res = await fetch(url, {
    method,
    headers,
    body,
    credentials: "include",
    signal: opts.signal,
    ...(opts.keepalive ? { keepalive: true } : {})
  });

  if (timedPath != null) {
    const ms = Math.round(performance.now() - timedPath);
    console.debug("[fetch-timing]", method, path, `${ms}ms`);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = { raw: text };
    }
  }

  if (!res.ok) {
    const code = pickString(parsed, "error") ?? `http_${res.status}`;
    const message = pickString(parsed, "message") ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, code, message, parsed);
  }

  return parsed as T;
}

function pickString(obj: unknown, key: string): string | undefined {
  if (!obj || typeof obj !== "object") return undefined;
  const v = (obj as Record<string, unknown>)[key];
  return typeof v === "string" ? v : undefined;
}

/**
 * Convenience wrapper that automatically retries once after refreshing the CSRF
 * token when a mutation returns 403 `csrf_invalid`. The auth provider injects
 * its own `refreshCsrf` callback so the retry path doesn't need to know how
 * tokens are minted.
 */
export async function apiCallWithCsrfRefresh<T>(
  path: string,
  opts: ApiCallOptions,
  refreshCsrf: () => Promise<string>
): Promise<T> {
  try {
    return await apiCall<T>(path, opts);
  } catch (err) {
    if (
      err instanceof ApiError &&
      err.status === 403 &&
      err.code === "csrf_invalid" &&
      opts.csrfToken !== undefined
    ) {
      const fresh = await refreshCsrf();
      return apiCall<T>(path, { ...opts, csrfToken: fresh });
    }
    throw err;
  }
}
